import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Select } from '@creditornot/cb-select';
import { Location, LocationDescriptor } from 'history';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';

import {
  getBasketSize,
  getSales,
  PerformanceData,
  PerformanceInsight,
  PerformanceOrderTypes,
} from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T, typographyCss } from 'common/typography';
import {
  ChartCategoryTelemetryEvent,
  telemetrySelectPerformanceChartInHomeInsightPage,
} from 'modules/insights-v2/telemetry';

import PerformanceBarChart from './PerformanceBarChart';
import TotalPerformance from './TotalPerformance';
import { BarChartDataByCategory, ChartCategory, InsightResponseWithPreviousInterval, OrderType } from '../../types';
import ErrorView from '../ErrorView';
import LoadingView from '../LoadingView';
import SectionHeader from '../SectionHeader';
import { LegendChartType } from '../charts/types';

type OverallPerformanceProps = {
  performanceInsight: InsightResponseWithPreviousInterval<PerformanceInsight>;
  isWoltPlusEnabled: boolean;
  link?: string | ((location: Location) => LocationDescriptor);
  isHeaderVisible?: boolean;
  isVATEnabled: boolean;
};
type PerformanceContentProps = {
  performanceInsight: InsightResponseWithPreviousInterval<PerformanceInsight>;
  isWoltPlusEnabled: boolean;
  isVATEnabled: boolean;
};

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const Root = styled.div`
  border: 1px solid ${({ theme }) => (theme.cbThemeMode === 'light' ? colors.pepper8 : colors.salt12)};
  border-radius: 8px;
  margin-inline: 24px;
  margin-block-start: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-inline: 16px;
  }
`;

const HeaderWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ChartSelector = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-block-end: 24px;
`;

const SelectWrapper = styled.div`
  min-width: 208px;
`;

const ChartWrapper = styled.div`
  padding: 24px 16px 16px;
`;

const PerformanceContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 431px;

  width: 100%;
`;

const transformToBarChartDataByCategory =
  (isWoltPlusEnabled: boolean, isVATEnabled: boolean) =>
  (acc: BarChartDataByCategory, curr: PerformanceData<string, PerformanceOrderTypes>): BarChartDataByCategory => {
    const { label: dateStr, split: splitByOrderType, total: totalPerDay } = curr;
    const {
      totalSales: currTotalSales,
      totalOrders: currTotalOrders,
      avgBasketSize: currAvgBasketSize,
      avgBasketSizeItems: currAvgBasketSizeItems,
    } = acc;
    const regular = splitByOrderType?.find(item => item.label === OrderType.Regular);
    const woltPlus = splitByOrderType?.find(item => item.label === OrderType.WoltPlus);

    return {
      totalSales: [
        ...currTotalSales,
        {
          name: dateStr || '',
          [OrderType.Regular]: isWoltPlusEnabled
            ? getSales(regular?.total, isVATEnabled) ?? 0
            : getSales(totalPerDay, isVATEnabled) ?? 0,
          [OrderType.WoltPlus]: getSales(woltPlus?.total, isVATEnabled) ?? 0,
        },
      ],
      totalOrders: [
        ...currTotalOrders,
        {
          name: dateStr || '',
          [OrderType.Regular]: isWoltPlusEnabled ? regular?.total?.orders ?? 0 : totalPerDay?.orders ?? 0,
          [OrderType.WoltPlus]: woltPlus?.total?.orders ?? 0,
        },
      ],
      avgBasketSize: [
        ...currAvgBasketSize,
        {
          name: dateStr || '',
          summaryValue: getBasketSize(totalPerDay, isVATEnabled),
          [OrderType.Regular]: isWoltPlusEnabled
            ? getBasketSize(regular?.total, isVATEnabled) ?? 0
            : getBasketSize(totalPerDay, isVATEnabled) ?? 0,
          [OrderType.WoltPlus]: getBasketSize(woltPlus?.total, isVATEnabled) ?? 0,
        },
      ],
      avgBasketSizeItems: [
        ...currAvgBasketSizeItems,
        {
          name: dateStr || '',
          summaryValue: totalPerDay?.avgBasketSizeItems,
          [OrderType.Regular]: isWoltPlusEnabled
            ? regular?.total?.avgBasketSizeItems ?? 0
            : totalPerDay?.avgBasketSizeItems ?? 0,
          [OrderType.WoltPlus]: woltPlus?.total?.avgBasketSizeItems ?? 0,
        },
      ],
    };
  };

const chartCategoryToTelemetryEventMap = {
  [ChartCategory.TOTAL_SALES]: ChartCategoryTelemetryEvent.TOTAL_SALES,
  [ChartCategory.TOTAL_ORDERS]: ChartCategoryTelemetryEvent.TOTAL_ORDERS,
  [ChartCategory.AVG_BASKET_SIZE]: ChartCategoryTelemetryEvent.AVG_BASKET_SIZES,
  [ChartCategory.AVG_BASKET_SIZE_ITEMS]: ChartCategoryTelemetryEvent.AVG_BASKET_SIZES_ITEMS,
};

const chartCategoryToNameMap = {
  [ChartCategory.TOTAL_SALES]: 'insights.performance.total-sales',
  [ChartCategory.TOTAL_ORDERS]: 'insights.performance.total-orders',
  [ChartCategory.AVG_BASKET_SIZE]: 'insights.performance.average-basket-size',
  [ChartCategory.AVG_BASKET_SIZE_ITEMS]: 'insights.performance.average-basket-size-items',
};

const chartCategoryList = Object.values(ChartCategory) as ChartCategory[];

const PerformanceContent = ({ performanceInsight, isWoltPlusEnabled, isVATEnabled }: PerformanceContentProps) => {
  const {
    selected: { data: performanceInsightForSelectedInterval },
    previous: { data: performanceInsightForPrevInterval },
  } = performanceInsight;

  const { formatMessage } = useIntl();

  const [selectedCategory, setSelectedCategory] = useQueryParam(
    'selectedCategory',
    withDefault(createEnumParam(chartCategoryList), ChartCategory.TOTAL_SALES, true),
    {
      removeDefaultsFromUrl: true,
    },
  );

  const chartOptions = useMemo(
    () =>
      chartCategoryList.map(value => ({
        label: formatMessage({ id: chartCategoryToNameMap[value] }),
        value,
      })),
    [formatMessage],
  );

  const barChartDataByCategory =
    performanceInsightForSelectedInterval?.data.subscription.split?.reduce<BarChartDataByCategory>(
      transformToBarChartDataByCategory(isWoltPlusEnabled, isVATEnabled),
      {
        totalSales: [],
        totalOrders: [],
        avgBasketSize: [],
        avgBasketSizeItems: [],
      },
    );
  const handleCategoryChange = (value: ChartCategory) => {
    setSelectedCategory(value);

    telemetrySelectPerformanceChartInHomeInsightPage(chartCategoryToTelemetryEventMap[value]);
  };

  const totalForSelectedDateRange = performanceInsightForSelectedInterval?.data.subscription.total;
  const totalForPreviousDateRange = performanceInsightForPrevInterval?.data.subscription.total;

  const legends = {
    [OrderType.Regular]: {
      color: colors.wolt88,
      value: formatMessage({ id: 'insights.order-type.regular' }),
      type: LegendChartType.BAR,
    },
    ...(isWoltPlusEnabled
      ? {
          [OrderType.WoltPlus]: {
            color: colors.wolt32,
            value: formatMessage({ id: 'insights.order-type.woltplus' }),
            type: LegendChartType.BAR,
          },
        }
      : {}),
  };

  return (
    <PerformanceContentWrapper>
      {performanceInsight.isFetching ? (
        <LoadingView />
      ) : performanceInsight.isError ? (
        <ErrorView onRetry={performanceInsight.refetch} />
      ) : (
        <>
          {totalForSelectedDateRange && (
            <>
              <TotalPerformance
                totalForSelectedDateRange={totalForSelectedDateRange}
                totalForPreviousDateRange={totalForPreviousDateRange}
                currency={performanceInsightForSelectedInterval.currency}
                legends={legends}
                isVATEnabled={isVATEnabled}
              />
              <Divider />
            </>
          )}
          <ChartWrapper>
            <ChartSelector>
              <T.Body3>
                <FormattedMessage id="insights.performance.show-chart-for" />
              </T.Body3>
              <SelectWrapper>
                <Select value={selectedCategory} options={chartOptions} onChange={handleCategoryChange} disableSearch />
              </SelectWrapper>
            </ChartSelector>
            <PerformanceBarChart
              data={barChartDataByCategory?.[selectedCategory] ?? []}
              isBarStacked={[ChartCategory.TOTAL_SALES, ChartCategory.TOTAL_ORDERS].includes(selectedCategory)}
              currency={
                [ChartCategory.TOTAL_SALES, ChartCategory.AVG_BASKET_SIZE].includes(selectedCategory)
                  ? performanceInsightForSelectedInterval?.currency
                  : undefined
              }
              summaryKey={chartCategoryToNameMap[selectedCategory]}
              legends={legends}
            />
          </ChartWrapper>
        </>
      )}
    </PerformanceContentWrapper>
  );
};

const SeeAllLink = styled(Link).attrs({ fontWeight: 'semiBold' })`
  ${typographyCss.Body2};
`;

const OverallPerformance = ({
  performanceInsight,
  isWoltPlusEnabled,
  link,
  isHeaderVisible = true,
  isVATEnabled,
}: OverallPerformanceProps) => {
  const { formatMessage } = useIntl();

  return (
    <Root>
      {isHeaderVisible && (
        <>
          <HeaderWrapper>
            <SectionHeader
              title={formatMessage({ id: 'insights.performance.header' })}
              tooltip={formatMessage({ id: 'insights.performance.tooltip' })}
            />
            {link && (
              <SeeAllLink to={link}>
                <FormattedMessage id="insights.see-all" />
              </SeeAllLink>
            )}
          </HeaderWrapper>
          <Divider />
        </>
      )}

      <PerformanceContent
        performanceInsight={performanceInsight}
        isWoltPlusEnabled={isWoltPlusEnabled ?? false}
        isVATEnabled={isVATEnabled}
      />
    </Root>
  );
};

export default OverallPerformance;
